import { PostHog } from 'posthog-js';

import { SimpleEvent } from '../../hooks/contexts/resaleEvent.context';
import { ResaleData } from '../../types/resale';
import { User } from '../../types/user';
import { logger } from '../../utils/logger';
import {
  ResaleBaseProperties,
  ResaleCheckoutRequestFailedProperties,
  ResaleCheckoutRequestStartedProperties,
  ResaleCheckoutRequestSuccessProperties,
  ResaleEventPropertiesMap,
  ResaleFastPassProperties,
  ResalePaymentCompletedProperties,
  ResalePaymentFailedProperties,
  ResalePaymentPageViewProperties,
  ResalePaymentProperties,
  ResaleTicketProperties,
} from './types';

interface FastPassTrackingInput {
  offerId: string;
  /** Math.round((finalPrice - saleValue) * 100), always >= 0 */
  spreadCents: number;
}

const buildFastPassProperties = (
  fastpass: FastPassTrackingInput | undefined
): ResaleFastPassProperties =>
  fastpass
    ? { is_fastpass_offer : true, fastpass_offer_id : fastpass.offerId, fastpass_spread_cents : fastpass.spreadCents }
    : { is_fastpass_offer : false };

/**
 * ResalePaymentTracking - Handles PostHog tracking for the resale payment page
 *
 * Responsibilities:
 * - Track payment page views
 * - Track checkout request lifecycle (started, success, failed)
 * - Track payment completion and failures
 * - Track iframe mounting and errors
 * - Track session expiration
 * - Build type-safe event properties with payment context
 */
export class ResalePaymentTracking {
  private posthog: PostHog | null;
  private debug: boolean;
  private checkoutStartTime: number | null = null;
  private pageLoadTime: number;

  constructor(posthog: PostHog | null, debug = false) {
    this.posthog = posthog;
    this.debug = debug;
    this.pageLoadTime = Date.now();
  }

  /**
   * Build base properties common to all resale payment events
   */
  private buildBaseProperties(
    resaleData: ResaleData | null,
    event: SimpleEvent | null,
    user: User | null,
    authenticatedUser: boolean
  ): ResaleBaseProperties | null {
    // Require at minimum resale UUID and event ID
    if (!resaleData?.uuid || !event?.id) {
      logger.warn('[ResalePaymentTracking] Missing required data for base properties', {
        hasResaleData : !!resaleData,
        hasEvent : !!event,
      });
      return null;
    }

    return {
      resale_id : resaleData.uuid,
      event_id : event.id,
      event_title : event.title,
      user_id : user?.id,
      is_authenticated : authenticatedUser,
      page_path : window.location.pathname,
      timestamp : new Date().toISOString(),
    };
  }

  /**
   * Build ticket properties from resale data
   */
  private buildTicketProperties(resaleData: ResaleData): ResaleTicketProperties {
    return {
      ticket_name : resaleData.ticket.guestType.name,
      session_name : undefined,
      price_cents : resaleData.ticket.originalPrice,
      price_formatted : `R$ ${(resaleData.ticket.originalPrice / 100).toFixed(2)}`,
      is_active : resaleData.isActive,
      is_sold : resaleData.isSold,
      is_deactivated : resaleData.isDeactivated,
    };
  }

  /**
   * Track payment page view
   * Called when user lands on the payment page
   */
  trackPageView(
    resaleData: ResaleData | null,
    event: SimpleEvent | null,
    user: User | null,
    authenticatedUser: boolean
  ): void {
    const baseProperties = this.buildBaseProperties(
      resaleData,
      event,
      user,
      authenticatedUser
    );

    if (!baseProperties) {
      logger.error(
        '[ResalePaymentTracking] Cannot track page view - missing required data'
      );
      return;
    }

    const properties: ResalePaymentPageViewProperties = {
      ...baseProperties,
      ...(resaleData ? this.buildTicketProperties(resaleData) : {}),
    };

    this.trackEvent('resale_payment_page_view', properties);
  }

  /**
   * Track checkout request started
   * Called when payment iframe begins checkout process
   */
  trackCheckoutRequestStarted(
    resaleData: ResaleData,
    event: SimpleEvent,
    user: User,
    fastpass?: FastPassTrackingInput
  ): void {
    this.checkoutStartTime = Date.now();

    const baseProperties = this.buildBaseProperties(resaleData, event, user, true);

    if (!baseProperties) {
      logger.error(
        '[ResalePaymentTracking] Cannot track checkout started - missing required data'
      );
      return;
    }

    const paymentProperties: ResalePaymentProperties = {
      checkout_status : 'pending',
    };

    const properties: ResaleCheckoutRequestStartedProperties = {
      ...baseProperties,
      ...this.buildTicketProperties(resaleData),
      ...paymentProperties,
      ...buildFastPassProperties(fastpass),
    };

    this.trackEvent('resale_checkout_request_started', properties);
  }

  /**
   * Track checkout request success
   * Called when shop-resale API returns success with transaction data
   */
  trackCheckoutRequestSuccess(
    resaleData: ResaleData,
    event: SimpleEvent,
    user: User,
    transactionId: string,
    paymentMethod?: string,
    fastpass?: FastPassTrackingInput
  ): void {
    const baseProperties = this.buildBaseProperties(resaleData, event, user, true);

    if (!baseProperties) {
      logger.error(
        '[ResalePaymentTracking] Cannot track checkout success - missing required data'
      );
      return;
    }

    const requestDuration = this.checkoutStartTime
      ? Date.now() - this.checkoutStartTime
      : undefined;

    const paymentProperties: ResalePaymentProperties = {
      transaction_id : transactionId,
      payment_method : paymentMethod,
      checkout_status : 'completed',
    };

    const properties: ResaleCheckoutRequestSuccessProperties = {
      ...baseProperties,
      ...this.buildTicketProperties(resaleData),
      ...paymentProperties,
      ...buildFastPassProperties(fastpass),
      request_duration_ms : requestDuration,
    };

    this.trackEvent('resale_checkout_request_success', properties);
  }

  /**
   * Track checkout request failed
   * Called when shop-resale API returns error
   */
  trackCheckoutRequestFailed(
    resaleData: ResaleData,
    event: SimpleEvent,
    user: User,
    errorMessage: string,
    errorCode?: string
  ): void {
    const baseProperties = this.buildBaseProperties(resaleData, event, user, true);

    if (!baseProperties) {
      logger.error(
        '[ResalePaymentTracking] Cannot track checkout failed - missing required data'
      );
      return;
    }

    const paymentProperties: ResalePaymentProperties = {
      checkout_status : 'failed',
    };

    const properties: ResaleCheckoutRequestFailedProperties = {
      ...baseProperties,
      ...this.buildTicketProperties(resaleData),
      ...paymentProperties,
      error_message : errorMessage,
      error_code : errorCode,
      error_type : 'api_error',
      error_context : 'shop_resale_api',
    };

    this.trackEvent('resale_checkout_request_failed', properties);
  }

  /**
   * Track payment completed
   * Called when payment is successfully processed
   */
  trackPaymentCompleted(
    resaleData: ResaleData,
    event: SimpleEvent,
    user: User,
    transactionId: string,
    paymentMethod?: string,
    fastpass?: FastPassTrackingInput
  ): void {
    const baseProperties = this.buildBaseProperties(resaleData, event, user, true);

    if (!baseProperties) {
      logger.error(
        '[ResalePaymentTracking] Cannot track payment completed - missing required data'
      );
      return;
    }

    const completionTime = Date.now() - this.pageLoadTime;

    const paymentProperties: ResalePaymentProperties = {
      transaction_id : transactionId,
      payment_method : paymentMethod,
      checkout_status : 'completed',
    };

    const properties: ResalePaymentCompletedProperties = {
      ...baseProperties,
      ...this.buildTicketProperties(resaleData),
      ...paymentProperties,
      ...buildFastPassProperties(fastpass),
      completion_time_ms : completionTime,
    };

    this.trackEvent('resale_payment_completed', properties);
  }

  /**
   * Track payment failed
   * Called when payment processing fails
   */
  trackPaymentFailed(
    resaleData: ResaleData,
    event: SimpleEvent,
    user: User,
    errorMessage: string,
    errorCode?: string,
    transactionId?: string
  ): void {
    const baseProperties = this.buildBaseProperties(resaleData, event, user, true);

    if (!baseProperties) {
      logger.error(
        '[ResalePaymentTracking] Cannot track payment failed - missing required data'
      );
      return;
    }

    const paymentProperties: ResalePaymentProperties = {
      transaction_id : transactionId,
      checkout_status : 'failed',
    };

    const properties: ResalePaymentFailedProperties = {
      ...baseProperties,
      ...this.buildTicketProperties(resaleData),
      ...paymentProperties,
      error_message : errorMessage,
      error_code : errorCode,
      error_type : 'api_error',
      error_context : 'payment_processing',
    };

    this.trackEvent('resale_payment_failed', properties);
  }

  /**
   * Track payment session expired
   * Called when user tries to access expired payment URL
   */
  trackPaymentSessionExpired(
    resaleId: string,
    errorMessage: string
  ): void {
    const properties: ResaleEventPropertiesMap['resale_payment_session_expired'] = {
      resale_id : resaleId,
      page_path : window.location.pathname,
      timestamp : new Date().toISOString(),
      error_message : errorMessage,
      error_type : 'validation_error',
      error_context : 'session_expired',
    };

    this.trackEvent('resale_payment_session_expired', properties);
  }

  /**
   * Track payment iframe mounted
   * Called when payment iframe successfully mounts
   */
  trackIframeMounted(
    resaleData: ResaleData,
    event: SimpleEvent,
    user: User,
    transactionId: string,
    mountTime: number
  ): void {
    const baseProperties = this.buildBaseProperties(resaleData, event, user, true);

    if (!baseProperties) {
      logger.error(
        '[ResalePaymentTracking] Cannot track iframe mounted - missing required data'
      );
      return;
    }

    const paymentProperties: ResalePaymentProperties = {
      transaction_id : transactionId,
      iframe_mounted : true,
      iframe_mount_time : mountTime,
    };

    const properties: ResaleEventPropertiesMap['resale_payment_iframe_mounted'] = {
      ...baseProperties,
      ...paymentProperties,
    };

    this.trackEvent('resale_payment_iframe_mounted', properties);
  }

  /**
   * Track payment iframe error
   * Called when payment iframe fails to mount or encounters error
   */
  trackIframeError(
    resaleData: ResaleData,
    event: SimpleEvent,
    user: User,
    errorMessage: string,
    transactionId?: string
  ): void {
    const baseProperties = this.buildBaseProperties(resaleData, event, user, true);

    if (!baseProperties) {
      logger.error(
        '[ResalePaymentTracking] Cannot track iframe error - missing required data'
      );
      return;
    }

    const paymentProperties: ResalePaymentProperties = {
      transaction_id : transactionId,
      iframe_mounted : false,
    };

    const properties: ResaleEventPropertiesMap['resale_payment_iframe_error'] = {
      ...baseProperties,
      ...paymentProperties,
      error_message : errorMessage,
      error_type : 'unknown',
      error_context : 'iframe_mount_failed',
    };

    this.trackEvent('resale_payment_iframe_error', properties);
  }

  /**
   * Generic track event method
   * Handles the actual PostHog capture with error handling
   */
  private trackEvent<T extends keyof ResaleEventPropertiesMap>(
    eventName: T,
    properties: ResaleEventPropertiesMap[T]
  ): void {
    try {
      if (!this.posthog) {
        if (this.debug) {
          logger.warn(
            '[ResalePaymentTracking] PostHog not initialized, event not tracked:',
            {
              eventName,
              properties,
            }
          );
        }
        return;
      }

      this.posthog.capture(eventName, properties);

      if (this.debug) {
        logger.info('[ResalePaymentTracking] Event tracked:', {
          eventName,
          properties,
        });
      }
    } catch (error) {
      logger.error('[ResalePaymentTracking] Failed to track event:', {
        eventName,
        error,
      });
    }
  }
}
